// The review screen's player with the video intro card (ADR-036 decision 5; mockups/script-review.html,
// playWithIntro). The first time the owner presses play, the card the learner will see is drawn over the
// video's area for about 3.5 s; then the video starts. Skip intro and Escape end it at once. It is
// shown once per player, never again on a pause or a seek, and only when play starts from the top.
//
// The card itself is the surface's IntroCard (one card for both players: its look, timing, Escape
// handling and reduced-motion behaviour live in @waypoint/surface); this file gives it this app's words,
// the lines this screen already has, and the tenant's logo. The lines:
//
//   Guide    the guide's name, as the Guide fact beside the script reads it (the first scene's citation)
//   Section  the version's `section` (the pack's entry for the title; absent on an older pack)
//   Topic    the title's name from the production plan
//   footer   "Version N · Published <d Month yyyy>", or "Version N · Not yet published" for a version
//            that is not live; and "Guide edition <edition>" on its own line
//
// A line whose value is missing is left out, never replaced with a placeholder. The pack's own edition
// (version 0, no tenant version yet) has no version line rather than "Version 0". The brand colour is
// --intro-brand, bound by the surface's stylesheet to the tenant theme's --wp-action; the logo is the
// tenant's logoRef, and without one the slot is empty.
//
// Where it differs from the mockup: the native controls are the browser's, so the first press of play
// is caught on the media's own play event (the video is paused again before it has shown a frame), and
// afterwards focus goes to the video element, the nearest thing to its Play control.
import { IntroCard, hasIntro, type IntroBrand, type IntroContent } from "@waypoint/surface";
import { useRef, useState } from "react";
import { t } from "../../i18n/t.js";
import { useTenantTheme } from "../../theme/theme-gate.js";
import { captionsPath, mediaPath, type VersionDetail } from "../../versions/model.js";
import { guideName } from "./issue-format.js";
import { mmss } from "./plan-model.js";
import type { TitleInfo } from "./script-format.js";

/** "3 November 2026", in UTC like the rest of the screen's dates, so it reads the same wherever opened. */
function formatDay(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso));
}

/** What the card says of this version; every line it has no value for is undefined. */
export function reviewIntro(version: VersionDetail, info: TitleInfo): IntroContent {
  const doc = version.script.scenes[0]?.citation.doc;
  const live = version.published?.at;
  const number = version.number;
  return {
    guide: doc === undefined ? undefined : guideName(doc),
    section: version.section,
    // Without the plan the title's name is its id: no topic rather than an id.
    topic: info.title === info.titleId ? undefined : info.title,
    version:
      version.source === "pack" || number < 1
        ? undefined
        : live === undefined
          ? t("review.intro.unpublished", { number })
          : t("review.intro.published", { number, date: formatDay(live) }),
    edition:
      version.guideEdition === undefined
        ? undefined
        : t("review.intro.edition", { edition: version.guideEdition }),
  };
}

export function ReviewPlayer({
  version,
  info,
  duration,
}: {
  readonly version: VersionDetail;
  readonly info: TitleInfo;
  readonly duration: number | undefined;
}) {
  const id = version.id ?? "";
  const video = useRef<HTMLVideoElement>(null);
  const theme = useTenantTheme();
  const [intro, setIntro] = useState<"idle" | "showing" | "done">("idle");
  const content = reviewIntro(version, info);
  const brand: IntroBrand | null =
    theme.logoRef === undefined
      ? null
      : { kind: "image", src: theme.logoRef, alt: theme.productName ?? "" };
  return (
    <div className="relative">
      <video
        ref={video}
        controls
        preload="metadata"
        src={mediaPath(id)}
        aria-label={t("review.ready.player", { time: mmss(duration ?? 0) })}
        tabIndex={intro === "showing" ? -1 : 0}
        className="aspect-video w-full rounded-8 border border-wp-line-strong bg-br-ink"
        onPlay={(e) => {
          // While the card shows, nothing plays under it (Space on a focused video, media keys).
          if (intro === "showing") {
            e.currentTarget.pause();
            return;
          }
          // The first press of play, from the top: the card first, then the video. A card with no
          // lines is not drawn at all, so the press plays.
          if (intro !== "idle" || e.currentTarget.currentTime > 0.05 || !hasIntro(content)) return;
          e.currentTarget.pause();
          setIntro("showing");
        }}
      >
        <track kind="captions" src={captionsPath(id)} srcLang="en" default />
      </video>
      {intro === "showing" ? (
        <IntroCard
          content={content}
          labels={{
            region: t("review.intro.region"),
            guide: t("review.intro.guide"),
            section: t("review.intro.section"),
            topic: t("review.intro.topic"),
            skip: t("review.intro.skip"),
          }}
          brand={brand}
          level={3}
          focus
          onDone={({ hadFocus }) => {
            setIntro("done");
            const el = video.current;
            if (el === null) return;
            if (hadFocus) el.focus({ preventScroll: true });
            // A refused play (the browser's own policy) leaves the native Play control to the owner.
            el.play().catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}
